import React, {useState, useEffect, Fragment} from 'react'
import {useDispatch, useSelector} from "react-redux"
import {format, parse, parseISO} from "date-fns"

// Material-UI Core
import {makeStyles} from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import Checkbox from '@material-ui/core/Checkbox'
import Box from '@material-ui/core/Box'
import Tooltip from '@material-ui/core/Tooltip'

// Material-UI Icon
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser'

// Material-UI Lab
import Pagination from '@material-ui/lab/Pagination'

// Custom Components
import EnhancedTableHead from './EnhancedTableHead'
import FileManagerToolbar from '../FileManager/FileManagerToolbar'

import SentSvgIcon from '../../img/sent.svg'

// Custom Style
import enhancedTableStyle from '../../styles/jss/components/table/enhancedTableStyle'
import { ContactsOutlined } from '@material-ui/icons'


const useStyles = makeStyles(theme => enhancedTableStyle(theme))

const EnhancedTable = props => {
	const classes = useStyles()
	const {
		fileManagerType,
		loading,
		folderRight,
		fileList,
		folderSelected,
		propertySelected,
		setPropertySelected,
		quickSearchKeyWord,
		setQuickSearchKeyWord,
		createFolderAction, 
		createFolderDialogHandleOpen, 
		confirmDialogHandleOpen, 
		confirmDialogHandleClose,
		removeFolderAction,
		removeFileAction,
		confirmDialogDeleteFileHandleOpen,
		propertyDownloadAction, 
		resetFileSelect, 
		setResetFileSelect, 
		resetMovePropertyControl, 
		setResetMovePropertyControl, 
		movePropertyAction, 
		propertyDetailDialogHandleOpen,
		advancedSearchDialogHandleOpen,
		setFileSearchDialogOpen,
		setFileUploaded
	} = props

	const dispatch = useDispatch()
	
	const [order, setOrder] = useState('desc')   // asc, desc
	const [orderBy, setOrderBy] = useState('modified')
	const [page, setPage] = useState(1)
	const [rowsPerPage, setRowsPerPage] = useState(20)

	// Redux Data
	const propertySeqRedux = useSelector(state => state.propertySeqRedux)
	const folderSelectedRedux = useSelector(state => state.folderSelectedRedux)
	
	const createData = (folderSeq, propertySeq, primaryPropertyFileSeq, totalFile, filename, organization, inOut, referenceNo, subject, modified, modifyBy, isConfidential, isCirculated, folderRight, folderPath, dateOfDocument) => {
		return {folderSeq, propertySeq, primaryPropertyFileSeq, totalFile, filename, organization, inOut, referenceNo, subject, modified, modifyBy, isConfidential, isCirculated, folderRight, folderPath, dateOfDocument}
	}
	
	const generateTableData = fileList => {
		let rows = []
	
		fileList.map(x => {
			let filename = x.filename !== null ? x.filename : ""
			let date_of_document = x.date_of_document !== null ? x.date_of_document : ""
			let company_name = x.company_name !== null ? x.company_name : ""
			let inout = x.inout !== null ? x.inout : ""
			let ref_no = x.ref_no !== null ? x.ref_no : ""
			let file_subject = x.file_subject !== null ? x.file_subject : ""
			let modifydate = x.modifydate !== null ? x.modifydate : ""
			let modifyuser = x.modifyuser !== null ? x.modifyuser : ""

			if (filename.toLowerCase().includes(quickSearchKeyWord.toLowerCase()) || company_name.toLowerCase().includes(quickSearchKeyWord.toLowerCase()) || inout.toLowerCase().includes(quickSearchKeyWord.toLowerCase()) || ref_no.toLowerCase().includes(quickSearchKeyWord.toLowerCase()) || 
			    file_subject.toLowerCase().includes(quickSearchKeyWord.toLowerCase()) || modifydate.includes(quickSearchKeyWord) || modifyuser.includes(quickSearchKeyWord) || date_of_document.includes(quickSearchKeyWord)) {

				rows = (folderSelected !== "-9999") ? [...rows, createData(x.folder_seq, x.property_seq, x.primary_property_file_seq, x.file_count, x.filename, x.company_name, x.inout, x.ref_no, x.file_subject, x.modifydate, x.modifyuser, x.is_confidential, x.is_circulated, x.folder_right, "", x.date_of_document)]
				: [...rows, createData(x.folder_seq, x.property_seq, x.primary_property_file_seq, x.file_count, x.filename, x.company_name, x.inout, x.ref_no, x.file_subject, x.modifydate, x.modifyuser, x.is_confidential, x.is_circulated, x.folder_right, x.folder_path, x.date_of_document)]
			}
		})

		return rows
	}
	
	const descendingComparator = (a, b, orderBy) => {
		if (b[orderBy] < a[orderBy]) {
			return -1
		}
	
		if (b[orderBy] > a[orderBy]) {
			return 1
		}
	
		return 0
	}
	
	const getComparator = (order, orderBy) => {
		return order === 'desc' 
		? (a, b) => descendingComparator(a, b, orderBy) 
		: (a, b) => -descendingComparator(a, b, orderBy)
	}
	
	const stableSort = (array, comparator) => {
		const stabilizedThis = array.map((el, index) => [el, index])
	
		stabilizedThis.sort((a, b) => {
			const order = comparator(a[0], b[0])
			
			if (order !== 0) return order
				return a[1] - b[1]
		})
	
		return stabilizedThis.map((el) => el[0])
	}
		
	const fileListHeader = [
		{id: 'totalFile', numeric: false, disablePadding: true, label: 'Att.'},
		{id: 'referenceNo', numeric: false, disablePadding: false, label: 'Reference No.'},
		{id: 'dateOfDocument', numeric: false, disablePadding: false, label: 'Date of Document'},
		{id: 'organization', numeric: false, disablePadding: false, label: 'Organization'},
		{id: 'subject', numeric: false, disablePadding: false, label: 'Subject'},
		{id: 'inOut', numeric: false, disablePadding: false, label: 'IN/OUT'},
		{id: 'filename', numeric: false, disablePadding: false, label: 'File Name'},
		{id: 'modified', numeric: false, disablePadding: false, label: 'Modified'}
	]

	const rows = generateTableData(fileList)

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc'
		setOrder(isAsc ? 'desc' : 'asc')
		setOrderBy(property)
	}

	const handleFileSelect = (event, name) => {
		const selectedIndex = propertySelected.indexOf(name)
		let newSelected = []

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(propertySelected, name)
		} 
		else if (selectedIndex === 0) {
			newSelected = newSelected.concat(propertySelected.slice(1))
			setResetMovePropertyControl(true)
		} 
		else if (selectedIndex === propertySelected.length - 1) {
			newSelected = newSelected.concat(propertySelected.slice(0, -1))
		} 
		else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				propertySelected.slice(0, selectedIndex),
				propertySelected.slice(selectedIndex + 1),
			)
		}

		setPropertySelected(newSelected)
		dispatch({
			type: "FILE_SELECT",
			data: newSelected
		})
	}

	const handleFileSelectAll = (event) => {
		if (event.target.checked) {
			// const newSelecteds = rows.map(n => n.propertySeq)
			let newSelecteds = []

			rows.map(row => {
				if (!row.isConfidential || row.isConfidential && row.folderRight.read_confidential) {
					newSelecteds.push(row.propertySeq)
				}
			})

			setPropertySelected(newSelecteds)
			dispatch({
				type: "FILE_SELECT",
				data: newSelecteds
			})

			return
		}

		handleResetFileSelect()
		setResetMovePropertyControl(true)
	}


	const handleResetFileSelect = () => {
		setPropertySelected([])

		dispatch({
			type: "FILE_DISSELECT",
			data: []
		})
	}

	const isFileSelected = (name) => propertySelected.indexOf(name) !== -1

	const handleChangePage = (event, newPage) => {
		setPage(newPage)
	}
	

	useEffect(() => {
		if (resetFileSelect) {
			setPropertySelected([])
			setResetFileSelect(false)

			dispatch({
				type: "FILE_DISSELECT",
				data: []
			})
		}
	}, [resetFileSelect])


	return (
		// console.log("File Property Seqance Selected | Variable: propertySelected (EnhancedTable.js)"),
		// console.log(propertySelected),

		// console.log("Source File List | Variable: fileList (EnhancedTable.js)"),
		// console.log(fileList),
		
		// console.log("---------------------------------------------------------------------------------"),


		<div className={classes.root}>
			<Box className={classes.toolbar}>
				<FileManagerToolbar
					fileManagerType={fileManagerType}
					folderRight={folderRight}
					numSelected={propertySelected.length} 
					propertySelected={propertySelected}
					folderSelected={folderSelected}
					createFolderAction={createFolderAction}
					createFolderDialogHandleOpen={createFolderDialogHandleOpen}
					confirmDialogHandleOpen={confirmDialogHandleOpen}
					confirmDialogHandleClose={confirmDialogHandleClose}
					removeFolderAction={removeFolderAction}
					removeFileAction={removeFileAction}
					propertyDownloadAction={propertyDownloadAction}

					confirmDialogDeleteFileHandleOpen={confirmDialogDeleteFileHandleOpen}
					resetMovePropertyControl={resetMovePropertyControl}
					setResetMovePropertyControl={setResetMovePropertyControl}
					movePropertyAction={movePropertyAction}
					setQuickSearchKeyWord={setQuickSearchKeyWord}
					advancedSearchDialogHandleOpen={advancedSearchDialogHandleOpen}
					setFileSearchDialogOpen={setFileSearchDialogOpen}
					setFileUploaded={setFileUploaded}

					setResetFileSelect={setResetFileSelect}
					
				/>
			</Box>

			<TableContainer id="scrollableDiv" className={classes.container}>
				<Table
					stickyHeader
					className={classes.table}
					aria-labelledby="tableTitle"
					size='small'
					aria-label="eFiling list"
				>

					<EnhancedTableHead
						headCells={fileListHeader}
						numSelected={propertySelected.length}
						order={order}
						orderBy={orderBy}
						onSelectAllClick={handleFileSelectAll}
						onRequestSort={handleRequestSort}
						// rowCount={rows.length}
						rowCount={propertySelected.length}
					/>

					<TableBody>
						{stableSort(rows, getComparator(order, orderBy))
							.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
							.map((row, index) => {

							const isItemSelected = isFileSelected(row.propertySeq)
							const labelId = `enhanced-table-checkbox-${index}`

							return (
								<Fragment key={row.propertySeq}>
									{folderSelected === "-9999" ?
										<TableRow style = {index % 2 ? {background: "rgba(255, 255, 230, 0.5)"} : {background : "white"}}>
											<TableCell></TableCell>
											<TableCell></TableCell>
											<TableCell colSpan={6}>{row.folderPath}</TableCell>
										</TableRow>
									: <TableRow></TableRow>}

									<TableRow
										hover
										role="checkbox"
										aria-checked={isItemSelected}
										tabIndex={-1}
										key={row.propertySeq}
										selected={isItemSelected}
										style = {index % 2 ? {background: "rgba(255, 255, 230, 0.5)"} : {background : "white"}}
									>
										<TableCell padding="checkbox">
											{
												!row.isConfidential ?
													<Checkbox
														checked={isItemSelected}
														inputProps={{'aria-labelledby': labelId}}
														onClick={(event) => handleFileSelect(event, row.propertySeq)}
													/>
												:
													row.folderRight.read_confidential ?
														<Checkbox
															checked={isItemSelected}
															inputProps={{'aria-labelledby': labelId}}
															onClick={(event) => handleFileSelect(event, row.propertySeq)}
														/>
													: ""
											}
										</TableCell>

										<TableCell >
											{
												!row.isConfidential ?
													<Tooltip title="Open File Detail">
														<OpenInBrowserIcon className={classes.openDetailIcon}  onClick={propertyDetailDialogHandleOpen(row.folderSeq, row.propertySeq, row.primaryPropertyFileSeq, row.filename)} />
													</Tooltip>
												:
													row.folderRight.read_confidential ?
														<Tooltip title="Open File Detail">
															<OpenInBrowserIcon className={classes.openDetailIcon}  onClick={propertyDetailDialogHandleOpen(row.folderSeq, row.propertySeq, row.primaryPropertyFileSeq, row.filename)} />
														</Tooltip>
													:
														<Tooltip title="No Permission to read confidential file">
															<OpenInBrowserIcon className={classes.openDetailIcon} />
														</Tooltip>
											}
										</TableCell>

										<TableCell >
											<Box className={classes.hintIcon}>
											{
												row.isConfidential ?
													row.folderRight.read_confidential ?
														<Box className={classes.hintIcon}>
														<Tooltip title="Confidential File (Unlocked)">
															<LockOpenIcon className={classes.confidentialIcon} />
														</Tooltip>
														</Box>
													:
														<Box className={classes.hintIcon}>
														<Tooltip title="Confidential File (Locked)">
															<LockIcon className={classes.confidentialIcon} />
														</Tooltip>
														</Box>
												: ""
											}
											{
												row.isCirculated ? 
													<Box className={classes.hintIcon}>
													<Tooltip title="Circulated">
														<img src={SentSvgIcon} className={classes.sentSvgicon}/>
													</Tooltip>
													</Box>
												: ""
											}
											</Box>
										</TableCell>

										<TableCell align="left" component="th" id={labelId} scope="row" padding="none" >{row.totalFile}</TableCell>
										<TableCell align="left">{row.referenceNo}</TableCell>
										<TableCell align="left">{row.dateOfDocument ? format(new Date(row.dateOfDocument), "yyyy-MM-dd") : ""}</TableCell>
										<TableCell align="left">{row.organization}</TableCell>
										<TableCell align="left">{row.subject}</TableCell>
										<TableCell align="left">{row.inOut}</TableCell>
										<TableCell align="left">{row.filename}</TableCell>
										<TableCell align="left"><div>{row.modified}</div><div>{row.modifyBy}</div></TableCell>
									</TableRow>
								</Fragment>
							)
						})}
					</TableBody>
				</Table>

				{loading}
			</TableContainer>

			<Pagination count={Math.ceil(rows.length / rowsPerPage)} variant="outlined" shape="rounded" onChange={handleChangePage} page={page} className={classes.tablePagination}/>
		</div>
	)
}


export default EnhancedTable